/**
 * Recognizer idle synchronization.
 *
 * A cancelled/stopped recognition session takes a moment to tear down
 * (service connection close, SDK cleanup). Starting a new session during
 * teardown rejects with `already_recognizing` — which surfaced to users as
 * "retry is broken" right after cancelling a recording. Callers await this
 * before starting a session so an immediate retry works.
 */

import NativeAzureSpeech from './NativeAzureSpeech';

/** Max wait for a previous recognition session to release the recognizer. */
const RECOGNIZER_IDLE_TIMEOUT_MS = 2000;

/** Poll interval while waiting for teardown. */
const RECOGNIZER_IDLE_POLL_MS = 50;

/**
 * Resolves true once the native recognizer is idle, or false after
 * RECOGNIZER_IDLE_TIMEOUT_MS. On timeout the caller may proceed — the native
 * module then rejects with `already_recognizing`, which is at least accurate.
 */
export async function waitForRecognizerIdle(): Promise<boolean> {
  const deadline = Date.now() + RECOGNIZER_IDLE_TIMEOUT_MS;

  for (;;) {
    let busy: boolean;
    try {
      busy = NativeAzureSpeech.isRecognizing();
    } catch {
      return true;
    }
    if (!busy) return true;
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, RECOGNIZER_IDLE_POLL_MS));
  }
}
